import { GENERATION_IN_PROGRESS_WARNING } from '@/utils/conversation-reconciliation';
import { useChatStore } from '@/store/useChatStore';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const GENERATION_GUARD_STATE_KEY = 'liftGenerationGuard';

/** Native dialog text for toolbar refresh / tab close (browsers ignore custom copy). */
export const BROWSER_LEAVE_CONFIRM_MESSAGE = `${GENERATION_IN_PROGRESS_WARNING}\n\nLeave this page anyway?`;

function isRefreshShortcut(event: KeyboardEvent): boolean {
  return (
    event.key === 'F5' ||
    ((event.key === 'r' || event.key === 'R') &&
      (event.ctrlKey || event.metaKey))
  );
}

function getLocationKey(pathname: string, search: string, hash: string): string {
  return `${pathname}${search}${hash}`;
}

function isExemptLeaveTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  return Boolean(
    target.closest('[data-generation-guard-exempt]') ||
      target.closest('[data-generation-leave-modal]')
  );
}

function getLinkLeaveAction(
  link: HTMLAnchorElement,
  navigate: ReturnType<typeof useNavigate>
): (() => void) | null {
  const href = link.getAttribute('href');
  if (!href || href.startsWith('#') || href.startsWith('javascript:')) {
    return null;
  }
  if (link.target === '_blank') return null;

  try {
    const url = new URL(href, window.location.origin);
    if (url.origin !== window.location.origin) {
      return () => {
        window.location.href = url.href;
      };
    }

    const path = getLocationKey(url.pathname, url.search, url.hash);
    return () => navigate(path);
  } catch {
    return null;
  }
}

export function useGenerationLeaveGuard(
  isBlocked: boolean,
  getIsBlocked?: () => boolean,
  getBlockedPath?: () => string | null
) {
  const location = useLocation();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const pendingActionRef = useRef<(() => void) | null>(null);
  const isModalOpenRef = useRef(false);
  const isBlockedRef = useRef(isBlocked);
  const getIsBlockedRef = useRef(getIsBlocked);
  const getBlockedPathRef = useRef(getBlockedPath);
  const modalLockedRef = useRef(false);
  const leaveApprovedRef = useRef(false);
  const trapPushedRef = useRef(false);
  const ignorePopStateRef = useRef(false);
  const browserBackPromptedRef = useRef(false);
  const wasBlockedRef = useRef(false);

  isModalOpenRef.current = isModalOpen;
  isBlockedRef.current = isBlocked;
  getIsBlockedRef.current = getIsBlocked;
  getBlockedPathRef.current = getBlockedPath;

  const isBlockedActive = useCallback(() => {
    return getIsBlockedRef.current?.() ?? isBlockedRef.current;
  }, []);

  const getGuardedPath = useCallback(() => {
    return getBlockedPathRef.current?.() ?? null;
  }, []);

  /** Path 1 — in-app actions: styled modal (keyboard refresh, links, header back). */
  const promptInAppLeave = useCallback(
    (action: () => void) => {
      if (!isBlockedActive()) {
        action();
        return;
      }

      if (isModalOpenRef.current) {
        pendingActionRef.current = action;
        return;
      }

      pendingActionRef.current = action;
      modalLockedRef.current = true;
      setIsModalOpen(true);
    },
    [isBlockedActive]
  );

  const releaseHistoryTrap = useCallback(() => {
    if (!trapPushedRef.current) return;

    ignorePopStateRef.current = true;
    trapPushedRef.current = false;
    window.history.back();
  }, []);

  /** Path 2 — browser back: styled modal when possible (history trap + route snap-back). */
  const promptBrowserBackLeave = useCallback(() => {
    if (browserBackPromptedRef.current || isModalOpenRef.current) return;

    browserBackPromptedRef.current = true;
    promptInAppLeave(() => {
      ignorePopStateRef.current = true;
      trapPushedRef.current = false;
      window.history.go(-2);
    });
  }, [promptInAppLeave]);

  const guardAction = useCallback(
    (action: () => void) => {
      promptInAppLeave(action);
    },
    [promptInAppLeave]
  );

  const handleStay = useCallback(() => {
    modalLockedRef.current = false;
    browserBackPromptedRef.current = false;
    setIsModalOpen(false);
    pendingActionRef.current = null;
  }, []);

  const clearGenerationGuard = useCallback(() => {
    useChatStore.getState().finishAssistantResponse();
  }, []);

  const handleLeave = useCallback(() => {
    modalLockedRef.current = false;
    browserBackPromptedRef.current = false;
    setIsModalOpen(false);
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    leaveApprovedRef.current = true;
    clearGenerationGuard();
    trapPushedRef.current = false;
    action?.();
  }, [clearGenerationGuard]);

  const resetBrowserBackPrompt = useCallback(() => {
    browserBackPromptedRef.current = false;
  }, []);

  // Path 1 — keyboard refresh and in-page link clicks.
  useEffect(() => {
    if (!isBlocked) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isRefreshShortcut(event)) return;
      event.preventDefault();
      promptInAppLeave(() => window.location.reload());
    };

    const handleClick = (event: MouseEvent) => {
      if (leaveApprovedRef.current || !isBlockedActive()) return;
      if (isExemptLeaveTarget(event.target)) return;
      if (event.defaultPrevented) return;

      const link = (event.target as Element).closest('a[href]');
      if (!(link instanceof HTMLAnchorElement)) return;

      const leaveAction = getLinkLeaveAction(link, navigate);
      if (!leaveAction) return;

      const guardedPath = getGuardedPath();
      const href = link.getAttribute('href');
      if (guardedPath && href) {
        try {
          const url = new URL(href, window.location.origin);
          const targetPath = getLocationKey(
            url.pathname,
            url.search,
            url.hash
          );
          if (targetPath === guardedPath) return;
        } catch {
          // Fall through and guard the navigation.
        }
      }

      event.preventDefault();
      event.stopPropagation();
      promptInAppLeave(leaveAction);
    };

    window.addEventListener('keydown', handleKeyDown, { capture: true });
    document.addEventListener('click', handleClick, { capture: true });
    return () => {
      window.removeEventListener('keydown', handleKeyDown, { capture: true });
      document.removeEventListener('click', handleClick, { capture: true });
    };
  }, [
    getGuardedPath,
    isBlocked,
    isBlockedActive,
    navigate,
    promptInAppLeave,
  ]);

  // Path 2a — browser back via history trap + popstate.
  useEffect(() => {
    if (isBlocked) {
      if (!wasBlockedRef.current) {
        leaveApprovedRef.current = false;
        browserBackPromptedRef.current = false;
      }
      wasBlockedRef.current = true;

      if (!trapPushedRef.current) {
        window.history.pushState({ [GENERATION_GUARD_STATE_KEY]: true }, '');
        trapPushedRef.current = true;
      }

      const handlePopState = () => {
        if (ignorePopStateRef.current) {
          ignorePopStateRef.current = false;
          return;
        }

        if (leaveApprovedRef.current || !isBlockedActive()) return;

        const blockedPath = getGuardedPath();
        if (!blockedPath) return;

        window.history.pushState({ [GENERATION_GUARD_STATE_KEY]: true }, '');
        trapPushedRef.current = true;

        const currentPath = getLocationKey(
          window.location.pathname,
          window.location.search,
          window.location.hash
        );

        if (currentPath !== blockedPath) {
          navigate(blockedPath, { replace: true });
        }

        promptBrowserBackLeave();
      };

      window.addEventListener('popstate', handlePopState, { capture: true });
      return () =>
        window.removeEventListener('popstate', handlePopState, {
          capture: true,
        });
    }

    if (trapPushedRef.current) {
      releaseHistoryTrap();
    }

    wasBlockedRef.current = false;
    browserBackPromptedRef.current = false;
  }, [
    getGuardedPath,
    isBlocked,
    isBlockedActive,
    navigate,
    promptBrowserBackLeave,
    releaseHistoryTrap,
  ]);

  // Path 2a — snap back if React Router navigates away before the modal opens.
  useLayoutEffect(() => {
    if (leaveApprovedRef.current || !isBlockedActive()) return;

    const blockedPath = getGuardedPath();
    if (!blockedPath) return;

    const currentPath = getLocationKey(
      location.pathname,
      location.search,
      location.hash
    );

    if (currentPath === blockedPath) {
      resetBrowserBackPrompt();
      return;
    }

    navigate(blockedPath, { replace: true });
    promptBrowserBackLeave();
  }, [
    getGuardedPath,
    isBlockedActive,
    location.hash,
    location.pathname,
    location.search,
    navigate,
    promptBrowserBackLeave,
    resetBrowserBackPrompt,
  ]);

  useEffect(() => {
    if (isBlocked || !isModalOpen || modalLockedRef.current) return;

    modalLockedRef.current = false;
    setIsModalOpen(false);
    pendingActionRef.current = null;
  }, [isBlocked, isModalOpen]);

  // Path 2b — toolbar refresh / tab close: native beforeunload (only option).
  useEffect(() => {
    if (!isBlocked) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (leaveApprovedRef.current || !isBlockedActive()) return;

      event.preventDefault();
      event.returnValue = BROWSER_LEAVE_CONFIRM_MESSAGE;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isBlocked, isBlockedActive]);

  return {
    isLeaveModalOpen: isModalOpen,
    handleStay,
    handleLeave,
    guardAction,
  };
}
